import VectorTileLayer from '@planet/maps/layer/VectorTile';
import VectorTileSource from '@planet/maps/source/VectorTile';
import MVT from 'ol/format/MVT.js';
import {Fill, Stroke, Style} from 'ol/style';
import {array, func, object, oneOfType, string} from 'prop-types';
import React, {useEffect, useMemo, useRef} from 'react';
import {GRANERO_BASE_URL} from '@app/config';
import {getDefaultFeatureStyle, getSelectedStyle} from '@app/styles/mapStyles';
import {getHashFromId} from '@app/util/granero';

const tileLoader = (tile, url, token) => {
  tile.setLoader((extent, resolution, featureProjection) => {
    fetch(url, {
      headers: {Authorization: `Bearer ${token}`},
    }).then(resp => {
      resp.arrayBuffer().then(buf => {
        tile.setFeatures(
          tile.getFormat().readFeatures(buf, {
            extent,
            featureProjection,
          })
        );
      });
    });
  });
};

const mvtFormat = new MVT();

const styleMapper = (styles, zIndex) => {
  return styles.map(style => {
    if (style['fill-color']) {
      return new Style({
        stroke: new Stroke({
          color: style['stroke-color'],
          width: style['stroke-width'],
        }),
        fill: new Fill({
          color: style['fill-color'],
        }),
        zIndex,
      });
    }
    return new Style({
      stroke: new Stroke({
        color: style['stroke-color'],
        width: style['stroke-width'],
      }),
      zIndex,
    });
  });
};

const CollectionLayer = ({
  collectionId,
  token,
  onClick,
  selectedFeatures = [],
  style: styleProp,
  selectedStyle: selectedStyleProp,
  ...props
}) => {
  const layerRef = useRef();

  const style = styleProp || getDefaultFeatureStyle();
  const selectedStyle = selectedStyleProp || getSelectedStyle();

  const tileURL = `${GRANERO_BASE_URL}/ogc/my/collections/${collectionId}/tiles/{z}/{x}/{y}`;

  const slimmedRefs = useMemo(
    () => selectedFeatures.map(f => getHashFromId(f)),
    [selectedFeatures]
  );

  useEffect(() => {
    if (!layerRef.current) {
      return;
    }

    const map = layerRef.current.get('map');
    if (!map || !onClick) {
      return;
    }

    const onMapClick = function (event) {
      layerRef.current.getFeatures(event.pixel).then(features => {
        onClick(event, features);
      });
    };
    map.on('click', onMapClick);

    return () => {
      map.un('click', onMapClick);
    };
  }, [layerRef, onClick]);

  const selectedGeometryStyle = feature => {
    const isSelected =
      slimmedRefs.indexOf(getHashFromId(feature.properties_['pl:ref'])) !== -1;

    if (isSelected) {
      return styleMapper(selectedStyle, Infinity);
    } else {
      return styleMapper(style);
    }
  };

  return (
    <VectorTileLayer ref={layerRef} style={selectedGeometryStyle} {...props}>
      <VectorTileSource
        url={tileURL}
        options={{
          format: mvtFormat,
          tileLoadFunction: (tile, url) => tileLoader(tile, url, token),
        }}
      />
    </VectorTileLayer>
  );
};

CollectionLayer.propTypes = {
  collectionId: string,
  token: string,
  onClick: func,
  selectedFeatures: array,
  style: oneOfType([array, func, object]),
  selectedStyle: oneOfType([array, func, object]),
};

export default CollectionLayer;
